import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import type { CallLogItem } from '../../types';
import { getApiErrorMessage } from '../../utils/errors';

interface UseCallLogsParams {
  callId: string;
  enabled: boolean;
}

interface UseCallLogsResult {
  logs: CallLogItem[];
  loading: boolean;
  error: string | null;
}

export function useCallLogs({ callId, enabled }: UseCallLogsParams): UseCallLogsResult {
  const { t } = useTranslation(['components']);
  const [logs, setLogs] = useState<CallLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedCallId, setLoadedCallId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (loadedCallId === callId) return;

    let mounted = true;
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<CallLogItem[]>(`/api/calls/${callId}/logs/`);
        if (!mounted) return;
        setLogs(Array.isArray(response.data) ? response.data : []);
        setLoadedCallId(callId);
      } catch (fetchError: unknown) {
        if (!mounted) return;
        setError(getApiErrorMessage(fetchError, t('components:callDetails.logsFetchError')));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLogs();
    return () => {
      mounted = false;
    };
  }, [callId, enabled, loadedCallId, t]);

  useEffect(() => {
    setLoadedCallId(null);
    setLogs([]);
    setError(null);
    setLoading(false);
  }, [callId]);

  return useMemo(
    () => ({ logs, loading, error }),
    [logs, loading, error],
  );
}
